import React, { useEffect, useState } from "react";
import { api, fmtMoney, fmtNumber } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Car, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import HireUsCTA from "@/components/HireUsCTA";
import EmailResultsCard from "@/components/EmailResultsCard";

export default function MileageCalc() {
  const { user } = useAuth();
  const { t } = useLang();
  const [distance, setDistance] = useState(50);
  const [rate, setRate] = useState(1.04);
  const [purpose, setPurpose] = useState("");
  const [vehicle, setVehicle] = useState("");
  const [entries, setEntries] = useState([]);

  const loadEntries = () => {
    if (!user) return;
    api.get("/mileage").then((r) => setEntries(r.data)).catch(() => {});
  };

  useEffect(() => { loadEntries(); }, [user]);

  const amount = (Number(distance) || 0) * (Number(rate) || 0);

  const save = async () => {
    if (!user) {
      toast.error("Sign in to save mileage");
      return;
    }
    try {
      await api.post("/mileage", {
        date: new Date().toISOString().slice(0, 10),
        distance_km: Number(distance) || 0,
        rate_per_km: Number(rate) || 0,
        purpose: purpose || null,
        vehicle: vehicle || null,
      });
      toast.success("Mileage logged");
      setDistance(0);
      setPurpose("");
      loadEntries();
    } catch {
      toast.error("Could not save");
    }
  };

  const remove = async (id) => {
    await api.delete(`/mileage/${id}`);
    loadEntries();
  };

  const totalKm = entries.reduce((s, e) => s + Number(e.distance_km || 0), 0);
  const totalAmt = entries.reduce((s, e) => s + Number(e.amount || 0), 0);

  return (
    <div className="stagger-in">
      <div className="label-mono">/ {t("common.calculator", "CALCULATOR")} · 03</div>
      <h1 className="font-display text-5xl tracking-tight mt-2">{t("mileage.h1", "Mileage Tracker")}</h1>
      <p className="text-zinc-400 mt-2">{t("mileage.sub", "IRD Tier 1 default — $1.04 per km (effective 1 Apr 2025)")}</p>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 mt-10 border border-[var(--border)]">
        <div className="lg:col-span-3 p-4 sm:p-6 md:p-8 border-r border-[var(--border)]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="label-mono mb-2">Distance (km)</div>
              <input
                type="number"
                step="0.1"
                className="input-sharp text-2xl py-4"
                value={distance}
                onChange={(e) => setDistance(e.target.value)}
                data-testid="mileage-distance-input"
              />
            </div>
            <div>
              <div className="label-mono mb-2">Rate per km</div>
              <input
                type="number"
                step="0.01"
                className="input-sharp text-2xl py-4"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                data-testid="mileage-rate-input"
              />
            </div>
            <div>
              <div className="label-mono mb-2">Purpose</div>
              <input
                className="input-sharp"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                placeholder="Site visit — 123 Queen St"
                data-testid="mileage-purpose-input"
              />
            </div>
            <div>
              <div className="label-mono mb-2">Vehicle</div>
              <input
                className="input-sharp"
                value={vehicle}
                onChange={(e) => setVehicle(e.target.value)}
                placeholder="Hilux"
                data-testid="mileage-vehicle-input"
              />
            </div>
          </div>
          <button
            onClick={save}
            disabled={!user}
            className="btn-primary mt-6"
            data-testid="mileage-save-btn"
          >
            <Plus size={14} /> Log trip
          </button>
          {!user && (
            <div className="text-xs font-mono text-zinc-500 mt-3">
              Sign in to save mileage history
            </div>
          )}
        </div>

        <div className="lg:col-span-2 p-4 sm:p-6 md:p-8 bg-[var(--surface)]">
          <div className="flex items-center gap-2">
            <Car size={16} className="text-[var(--primary)]" />
            <div className="label-mono">This trip</div>
          </div>
          <div className="mt-4 space-y-3 font-mono">
            <Row label="Distance" value={`${fmtNumber(distance, 1)} km`} />
            <Row label="Rate" value={`${fmtMoney(rate)} / km`} />
            <div className="border-t border-[var(--border)] pt-3">
              <Row label="Claim" value={fmtMoney(amount)} big accent testid="mileage-amount" />
            </div>
          </div>
          <div className="hi-vis-stripe h-1.5 mt-8 -mx-4 sm:-mx-6 md:-mx-8 -mb-4 sm:-mb-6 md:-mb-8" />
        </div>
      </div>

      {user && (
        <div className="mt-10">
          <div className="flex items-center justify-between mb-4">
            <div className="label-mono">/ HISTORY</div>
            <div className="text-xs font-mono text-zinc-400">
              {fmtNumber(totalKm, 1)} km · {fmtMoney(totalAmt)}
            </div>
          </div>
          <div className="border border-[var(--border)] overflow-x-auto" data-testid="mileage-list">
            <div className="min-w-[680px]">
            <div className="grid grid-cols-12 gap-0 text-xs font-mono uppercase tracking-wider text-zinc-500 border-b border-[var(--border)]">
              <div className="col-span-2 p-3">Date</div>
              <div className="col-span-3 p-3 border-l border-[var(--border)]">Purpose</div>
              <div className="col-span-2 p-3 border-l border-[var(--border)]">Vehicle</div>
              <div className="col-span-2 p-3 border-l border-[var(--border)]">KM</div>
              <div className="col-span-2 p-3 border-l border-[var(--border)]">Amount</div>
              <div className="col-span-1 p-3 border-l border-[var(--border)]" />
            </div>
            {entries.length === 0 && (
              <div className="p-6 text-center text-zinc-500 font-mono text-sm">
                No trips logged yet
              </div>
            )}
            {entries.map((e) => (
              <div
                key={e.id}
                className="grid grid-cols-12 gap-0 text-sm font-mono border-b border-[var(--border)] last:border-b-0"
              >
                <div className="col-span-2 p-3">{e.date}</div>
                <div className="col-span-3 p-3 border-l border-[var(--border)] truncate">
                  {e.purpose || "—"}
                </div>
                <div className="col-span-2 p-3 border-l border-[var(--border)] truncate">
                  {e.vehicle || "—"}
                </div>
                <div className="col-span-2 p-3 border-l border-[var(--border)]">
                  {fmtNumber(e.distance_km, 1)}
                </div>
                <div className="col-span-2 p-3 border-l border-[var(--border)] text-[var(--primary)]">
                  {fmtMoney(e.amount)}
                </div>
                <button
                  onClick={() => remove(e.id)}
                  className="col-span-1 border-l border-[var(--border)] text-zinc-500 hover:text-[var(--error)] flex items-center justify-center"
                  data-testid={`mileage-del-${e.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            </div>
          </div>
        </div>
      )}

      <EmailResultsCard
        calculator="mileage"
        payload={{
          distance_km: Number(distance) || 0,
          rate_per_km: Number(rate) || 0,
          amount,
          purpose,
          vehicle,
        }}
        labelKey="email.mileage.label"
        descKey="email.mileage.desc"
      />

      <HireUsCTA
        campaign="mileage-calc"
        compact
        headline="Mileage logged. Tax claim sorted?"
        subline="Logging km is one piece — claiming it correctly on your return is another. We bake it into our monthly tax estimates."
      />
    </div>
  );
}

function Row({ label, value, big, accent, testid }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="label-mono">{label}</span>
      <span
        data-testid={testid}
        className={`${big ? "text-3xl" : "text-base"} ${
          accent ? "text-[var(--primary)]" : "text-white"
        }`}
      >
        {value}
      </span>
    </div>
  );
}
